import {
  Config,
  Context,
  Effect,
  Layer,
  Option,
  Redacted,
  Runtime,
  pipe,
} from "effect";
import {
  ChainId,
  ChainKey,
  PublicClient,
  chains,
  getChainKeyFromProtocol,
} from "./chain";
import { ProtocolKey } from "./protocol";
import { FallbackTransport, createPublicClient, fallback, http } from "viem";
import { MissingData } from "effect/ConfigError";
import {
  RpcMetricsService,
  RpcMetricsServiceLive,
  updateRpcMetrics,
} from "./rpc-metrics";

export interface RpcConfig {
  readonly DRPC_KEY: Redacted.Redacted<string>;
  readonly INFURA_KEY: Redacted.Redacted<string>;
  readonly ALCHEMY_KEY: Redacted.Redacted<string>;
}

export class RpcConfigService extends Context.Tag("RpcConfigService")<
  RpcConfigService,
  RpcConfig
>() {}

export const ServerRpcConfig = Layer.effect(
  RpcConfigService,
  Effect.gen(function* () {
    const DRPC_KEY = yield* Config.redacted("DRPC_KEY");
    const INFURA_KEY = yield* Config.redacted("INFURA_KEY");
    const ALCHEMY_KEY = yield* Config.redacted("ALCHEMY_KEY");

    return {
      DRPC_KEY,
      INFURA_KEY,
      ALCHEMY_KEY,
    };
  })
);

export const BrowserRpcConfig = Layer.effect(
  RpcConfigService,
  Effect.gen(function* () {
    /**
     * Cannot use the normal Config method for these variables since Next js does not support dynamic client-side vars
     *
     * https://nextjs.org/docs/pages/building-your-application/configuring/environment-variables
     */
    const DRPC_KEY = yield* pipe(
      process.env.NEXT_PUBLIC_DRPC_KEY,
      Option.fromNullable,
      Option.map(Redacted.make),
      Effect.mapError(() => MissingData([], "missing NEXT_PUBLIC_DRPC_KEY"))
    );

    const INFURA_KEY = yield* pipe(
      process.env.NEXT_PUBLIC_INFURA_KEY,
      Option.fromNullable,
      Option.map(Redacted.make),
      Effect.mapError(() => MissingData([], "missing NEXT_PUBLIC_INFURA_KEY"))
    );

    const ALCHEMY_KEY = yield* pipe(
      process.env.NEXT_PUBLIC_ALCHEMY_KEY,
      Option.fromNullable,
      Option.map(Redacted.make),
      Effect.mapError(() => MissingData([], "missing NEXT_PUBLIC_ALCHEMY_KEY"))
    );

    return {
      DRPC_KEY,
      INFURA_KEY,
      ALCHEMY_KEY,
    };
  })
);

export interface PublicClientServiceInterface {
  readonly getPublicClientByChain: (chainKey: ChainKey) => PublicClient;
  readonly getPublicClient: (protocolKey: ProtocolKey) => PublicClient;
}

export class PublicClientService extends Context.Tag("PublicClientService")<
  PublicClientService,
  PublicClientServiceInterface
>() {}

export const PublicClientServiceLive = Layer.effect(
  PublicClientService,
  Effect.gen(function* () {
    const rpcConfig = yield* RpcConfigService;
    const rpcMetricsRef = yield* RpcMetricsService;

    /**
     * Using this setup to run an effect each time the onFetchRequest callback is invoked.
     *
     * Approach was taken from here: https://effect.website/docs/integrations/express
     */
    const runFork = Runtime.runFork(yield* Effect.runtime<never>());

    const transports = createTransports(rpcConfig, (request) => {
      runFork(updateRpcMetrics(request, rpcMetricsRef));
    });

    function getPublicClientByChain(chainKey: ChainKey): PublicClient {
      const chain = chains[chainKey];
      const transport = transports[chain.id];

      return createPublicClient({ chain, transport });
    }

    function getPublicClient(protocolKey: ProtocolKey): PublicClient {
      return pipe(protocolKey, getChainKeyFromProtocol, getPublicClientByChain);
    }

    return {
      getPublicClientByChain,
      getPublicClient,
    };
  })
);

export const BrowserPublicClientServiceLive = pipe(
  PublicClientServiceLive,
  Layer.provide(BrowserRpcConfig),
  Layer.provideMerge(RpcMetricsServiceLive)
);

export const ServerPublicClientServiceLive = pipe(
  PublicClientServiceLive,
  Layer.provide(ServerRpcConfig),
  Layer.provideMerge(RpcMetricsServiceLive)
);

export class ChainPublicClientService extends Context.Tag(
  "ChainPublicClientService"
)<ChainPublicClientService, { publicClient: PublicClient }>() {}

const { ethereum, arbitrum, base, optimism, local, anvil } = chains;

const INFURA_BATCH_SIZE = 32;
const ALCHEMY_BATCH_SIZE = 32;

/**
 * Sets up the transports needed to configure wagmi or create a viem public client.
 */
export function createTransports(
  rpcConfig: RpcConfig,
  onFetchRequest?: (request: Request) => void
): Record<ChainId, FallbackTransport> {
  const DRPC_KEY = Redacted.value(rpcConfig.DRPC_KEY);
  const INFURA_KEY = Redacted.value(rpcConfig.INFURA_KEY);
  const ALCHEMY_KEY = Redacted.value(rpcConfig.ALCHEMY_KEY);

  return {
    [ethereum.id]: fallback([
      http(`https://eth-mainnet.g.alchemy.com/v2/${ALCHEMY_KEY}`, {
        batch: {
          batchSize: ALCHEMY_BATCH_SIZE,
        },
        onFetchRequest,
      }),
      http(`https://mainnet.infura.io/v3/${INFURA_KEY}`, {
        batch: {
          batchSize: INFURA_BATCH_SIZE,
        },
        onFetchRequest,
      }),
      http(`https://lb.drpc.org/ogrpc?network=ethereum&dkey=${DRPC_KEY}`, {
        batch: true,
        onFetchRequest,
      }),
    ]),
    [arbitrum.id]: fallback([
      http(`https://arb-mainnet.g.alchemy.com/v2/${ALCHEMY_KEY}`, {
        batch: {
          batchSize: ALCHEMY_BATCH_SIZE,
        },
        onFetchRequest,
      }),
      http(`https://arbitrum-mainnet.infura.io/v3/${INFURA_KEY}`, {
        batch: {
          batchSize: INFURA_BATCH_SIZE,
        },
        onFetchRequest,
      }),
      http(`https://lb.drpc.org/ogrpc?network=arbitrum&dkey=${DRPC_KEY}`, {
        batch: true,
        onFetchRequest,
      }),
    ]),
    [base.id]: fallback([
      http(`https://base-mainnet.g.alchemy.com/v2/${ALCHEMY_KEY}`, {
        batch: {
          batchSize: ALCHEMY_BATCH_SIZE,
        },
        onFetchRequest,
      }),
      http(`https://base-mainnet.infura.io/v3/${INFURA_KEY}`, {
        batch: {
          batchSize: INFURA_BATCH_SIZE,
        },
        onFetchRequest,
      }),
      http(`https://lb.drpc.org/ogrpc?network=base&dkey=${DRPC_KEY}`, {
        batch: true,
        onFetchRequest,
      }),
    ]),
    [optimism.id]: fallback([
      http(`https://opt-mainnet.g.alchemy.com/v2/${ALCHEMY_KEY}`, {
        batch: {
          batchSize: ALCHEMY_BATCH_SIZE,
        },
        onFetchRequest,
      }),
      http(`https://optimism-mainnet.infura.io/v3/${INFURA_KEY}`, {
        batch: {
          batchSize: INFURA_BATCH_SIZE,
        },
        onFetchRequest,
      }),
      http(`https://lb.drpc.org/ogrpc?network=optimism&dkey=${DRPC_KEY}`, {
        batch: true,
        onFetchRequest,
      }),
    ]),
    [local.id]: fallback([http(undefined, { onFetchRequest })]),
    [anvil.id]: fallback([http(undefined, { onFetchRequest })]),
  };
}
